import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import FreelancerSidebarLayout from "./FreelancerSidebarLayout";
import { getSubscriptionAccessView } from "../../utils/subscriptionAccessState";
import "./FreelancerSubscription.css";

function FreelancerSubscription() {
  const [loading, setLoading] = useState(true);
  const [freelancerProfile, setFreelancerProfile] = useState(null);
  const [subscriptionInfo, setSubscriptionInfo] = useState(null);
  const [upcomingInvoice, setUpcomingInvoice] = useState(null);
  const [platformSettings, setPlatformSettings] = useState(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showSubscribeConfirmModal, setShowSubscribeConfirmModal] =
    useState(false);
  const [showCancelSuccessModal, setShowCancelSuccessModal] = useState(false);
  const [processing, setProcessing] = useState(false);
  // A signup promo code, typed HERE in OGESTYLE rather than on Stripe's payment page (2026-10-29).
  const [promoCode, setPromoCode] = useState("");
  const [promoPreview, setPromoPreview] = useState(null);
  const [promoError, setPromoError] = useState("");
  const [validatingPromo, setValidatingPromo] = useState(false);
  // Hidden until the server confirms the feature is switched on for this deployment.
  const [promoEnabled, setPromoEnabled] = useState(false);
  const navigate = useNavigate();

  const getAuthHeaders = () => {
    const token = localStorage.getItem("ogestyle_token");
    if (!token) {
      navigate("/login");
      return null;
    }
    return { Authorization: `Bearer ${token}` };
  };

  const getApiBaseUrl = () => {
    return process.env.REACT_APP_API_BASE_URL || "https://api.ogestyle.com";
  };

  useEffect(() => {
    fetchSubscriptionData();
  }, []);

  const fetchSubscriptionData = async () => {
    try {
      setLoading(true);
      const headers = getAuthHeaders();
      if (!headers) return;

      const apiBaseUrl = getApiBaseUrl();

      // Fetch freelancer profile, subscription info, and upcoming invoice in parallel
      const [profileRes, subscriptionRes, upcomingRes] = await Promise.all([
        axios.get(`${apiBaseUrl}/api/freelancer/profile`, { headers }),
        axios.get(`${apiBaseUrl}/api/subscription-new/status`, { headers }),
        axios
          .get(`${apiBaseUrl}/api/subscription-new/upcoming-invoice`, { headers })
          .catch(() => null),
      ]);

      setFreelancerProfile(profileRes.data.freelancer || profileRes.data);
      setSubscriptionInfo(subscriptionRes.data);
      setUpcomingInvoice(upcomingRes?.data?.upcomingInvoice || null);
      // Pricing is included in subscriptionRes.data.pricing
      setPlatformSettings(subscriptionRes.data.pricing);

      // A 404 means the plans feature is switched off, so the promo field must not exist at all.
      try {
        await axios.post(`${apiBaseUrl}/api/platform-plans/promo/validate`, { code: "" });
        setPromoEnabled(true);
      } catch (probeErr) {
        setPromoEnabled(probeErr.response?.status !== 404);
      }
    } catch (err) {
      if (err.response?.status === 401) {
        navigate("/login");
      }
    } finally {
      setLoading(false);
    }
  };

  /**
   * The server prices the discount; this only shows what it returned, so the two cannot drift.
   */
  const applyPromoCode = async () => {
    const code = promoCode.trim();
    setPromoError("");
    setPromoPreview(null);
    if (!code) {
      setPromoError("Enter a code.");
      return;
    }

    setValidatingPromo(true);
    try {
      const response = await axios.post(
        `${getApiBaseUrl()}/api/platform-plans/promo/validate`,
        { code, interval: "month" },
      );
      if (!response.data?.valid) {
        setPromoError(response.data?.message || "That code is not valid.");
        return;
      }
      setPromoPreview(response.data);
    } catch (err) {
      setPromoError(
        err.response?.status === 404
          ? "Promo codes are not available right now."
          : err.response?.data?.message || "Unable to check that code right now.",
      );
    } finally {
      setValidatingPromo(false);
    }
  };
  const handleSubscribe = async () => {
    try {
      setProcessing(true);
      const headers = getAuthHeaders();
      if (!headers) return;

      const apiBaseUrl = getApiBaseUrl();

      // Create Stripe subscription checkout session for freelancer
      // A freelancer subscribes through the route that validates a promo code (2026-10-29).
      // /api/subscription/create knew nothing about codes, so one entered at signup was ignored.
      const response = await axios.post(
        `${apiBaseUrl}/api/subscription-new/create-checkout-session`,
        {
          isFreelancer: true,
          ...(promoCode ? { promoCode } : {}),
        },
        { headers },
      );

      // Redirect to Stripe checkout
      // The new route returns `url`; still accept `checkoutUrl` so nothing else reading this breaks.
      const checkoutUrl = response.data.url || response.data.checkoutUrl;
      if (checkoutUrl) {
        window.location.href = checkoutUrl;
      }
    } catch (err) {
      setShowSubscribeConfirmModal(false);
      alert("Failed to start subscription. Please try again.");
    } finally {
      setProcessing(false);
    }
  };

  const handleCancelSubscription = async () => {
    try {
      setProcessing(true);
      const headers = getAuthHeaders();
      if (!headers) return;

      const apiBaseUrl = getApiBaseUrl();

      await axios.post(
        `${apiBaseUrl}/api/subscription-new/cancel`,
        {},
        { headers },
      );

      setShowCancelModal(false);
      setShowCancelSuccessModal(true);
      fetchSubscriptionData();
    } catch (err) {
      setShowCancelModal(false);
      alert("Failed to cancel subscription. Please try again.");
    } finally {
      setProcessing(false);
    }
  };

  const handleReactivateSubscription = async () => {
    try {
      setProcessing(true);
      const headers = getAuthHeaders();
      if (!headers) return;

      const apiBaseUrl = getApiBaseUrl();

      await axios.post(
        `${apiBaseUrl}/api/subscription-new/reactivate`,
        {},
        { headers },
      );

      alert("Subscription reactivated successfully!");
      fetchSubscriptionData();
    } catch (err) {
      alert("Failed to reactivate subscription. Please try again.");
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <FreelancerSidebarLayout>
        <div className="freelancer-subscription-page">
          <div className="loading-spinner">
            Loading subscription information...
          </div>
        </div>
      </FreelancerSidebarLayout>
    );
  }

  // Three-state trial system
  const isOnboarding = subscriptionInfo?.trialStatus === "not_started";
  const isInActiveTrial = subscriptionInfo?.trialStatus === "active";
  const isTrialExpired = subscriptionInfo?.trialStatus === "expired";

  // Wording for the access callout comes from accessState, so a cancellation or a failed
  // payment is never described as a trial that ended (utils/subscriptionAccessState.js).
  const accessView = getSubscriptionAccessView(subscriptionInfo);

  const isOnTrial = subscriptionInfo?.paymentModel === "TRIAL";
  const isOnSubscription = subscriptionInfo?.paymentModel === "SUBSCRIPTION";
  const isOnTransaction = subscriptionInfo?.paymentModel === "TRANSACTION";
  const gracePeriodActive =
    subscriptionInfo?.subscriptionGracePeriodEndsAt &&
    new Date(subscriptionInfo.subscriptionGracePeriodEndsAt) > new Date();

  // Calculate  trial days dynamically from trial end date
  const getTrialDaysText = () => {
    if (!subscriptionInfo?.trialEndDate || !subscriptionInfo?.trialStartDate) {
      return "trial";
    }
    const start = new Date(subscriptionInfo.trialStartDate);
    const end = new Date(subscriptionInfo.trialEndDate);
    const days = Math.round((end - start) / (1000 * 60 * 60 * 24));
    return `${days}-day trial`;
  };

  const monthlyPrice = platformSettings?.freelancerMonthly;

  return (
    <FreelancerSidebarLayout>
      <div className="freelancer-subscription-page">
        <div className="subscription-container">
          <h1>💳 Subscription Management</h1>

          {/* Current Status */}
          <div className="status-card">
            <h2>Current Plan</h2>
            <div className="status-info">
              <div className="status-row">
                <span className="label">Payment Model:</span>
                <span
                  className={`badge ${isOnSubscription ? "subscription" : isOnTrial ? "trial" : "transaction"}`}
                >
                  {subscriptionInfo?.paymentModel}
                </span>
              </div>

              {/* Onboarding State - Trial Not Started Yet */}
              {isOnboarding && (
                <div className="trial-notice" style={{
                  background: "linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)",
                  color: "#1e40af",
                  padding: "16px",
                  borderRadius: "8px",
                  marginTop: "12px",
                  border: "2px solid #3b82f6"
                }}>
                  <div style={{ fontSize: "24px", marginBottom: "8px" }}>🚀</div>
                  <strong>Welcome to OGESTYLE!</strong>
                  <p style={{ margin: "8px 0 0 0" }}>
                    Your trial will start when you activate your freelancer account. 
                    Take your time completing your profile and setup.
                  </p>
                </div>
              )}

              {/* Active Trial */}
              {isInActiveTrial && (
                <>
                  <div className="status-row">
                    <span className="label">Trial Ends:</span>
                    <span className="value">
                      {new Date(
                        subscriptionInfo.trialEndDate,
                      ).toLocaleDateString()}
                    </span>
                  </div>
                  <div className="trial-notice" style={{
                    background: "linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)",
                    color: "#065f46",
                  }}>
                    Your {getTrialDaysText()} gives you full access to all
                    features. After trial ends, you'll need to choose a payment
                    model.
                  </div>
                </>
              )}

              {/* Access callout — driven by accessState, not by the trial dates. */}
              {accessView.blocking && (
                <div className="trial-notice" style={{
                  background: accessView.background,
                  color: "white",
                  padding: "16px",
                  borderRadius: "8px",
                  marginTop: "12px",
                }}>
                  <div style={{ fontSize: "24px", marginBottom: "8px" }}>⚡</div>
                  <strong>{accessView.title}</strong>
                  <p style={{ margin: "8px 0 0 0" }}>{accessView.message}</p>
                  {accessView.action === "reactivate" && (
                    <button
                      type="button"
                      onClick={handleReactivateSubscription}
                      style={{
                        marginTop: "12px",
                        background: "white",
                        color: accessView.accent,
                        border: "none",
                        padding: "10px 20px",
                        borderRadius: "8px",
                        fontWeight: 700,
                        cursor: "pointer",
                      }}
                    >
                      {accessView.ctaLabel}
                    </button>
                  )}
                  {accessView.action === "add_card" && (
                    <button
                      type="button"
                      onClick={() =>
                        navigate("/dashboard/freelancer/subscription/payment")
                      }
                      style={{
                        marginTop: "12px",
                        background: "white",
                        color: accessView.accent,
                        border: "none",
                        padding: "10px 20px",
                        borderRadius: "8px",
                        fontWeight: 700,
                        cursor: "pointer",
                      }}
                    >
                      {accessView.ctaLabel}
                    </button>
                  )}
                </div>
              )}

              {isOnSubscription && (
                <>
                  <div className="status-row">
                    <span className="label">Subscription Status:</span>
                    <span
                      className={`badge ${subscriptionInfo.subscriptionStatus}`}
                    >
                      {subscriptionInfo.subscriptionStatus}
                    </span>
                  </div>
                  <div className="status-row">
                    <span className="label">Current Period:</span>
                    <span className="value">
                      {upcomingInvoice?.periodStart
                        ? new Date(upcomingInvoice.periodStart * 1000).toLocaleDateString()
                        : subscriptionInfo.subscriptionCurrentPeriodStart &&
                          new Date(subscriptionInfo.subscriptionCurrentPeriodStart).getTime() > 0
                        ? new Date(subscriptionInfo.subscriptionCurrentPeriodStart).toLocaleDateString()
                        : "\u2014"}
                      {" - "}
                      {upcomingInvoice?.periodEnd
                        ? new Date(upcomingInvoice.periodEnd * 1000).toLocaleDateString()
                        : subscriptionInfo.subscriptionCurrentPeriodEnd &&
                          new Date(subscriptionInfo.subscriptionCurrentPeriodEnd).getTime() > 0
                        ? new Date(subscriptionInfo.subscriptionCurrentPeriodEnd).toLocaleDateString()
                        : "—"}
                    </span>
                  </div>
                  <div className="status-row">
                    <span className="label">Monthly Amount:</span>
                    <span className="value amount">
                      ${subscriptionInfo.subscriptionAmount || monthlyPrice}/mo
                    </span>
                  </div>
                  {subscriptionInfo.subscriptionCancelAtPeriodEnd && (
                    <div className="cancel-notice">
                      ⚠️ Your subscription will cancel on{" "}
                      {subscriptionInfo.subscriptionCurrentPeriodEnd &&
                      new Date(subscriptionInfo.subscriptionCurrentPeriodEnd).getTime() > 0
                        ? new Date(subscriptionInfo.subscriptionCurrentPeriodEnd).toLocaleDateString()
                        : "the end of your billing period"}
                        . After that, your profile goes inactive to customers
                        and you stop appearing in public searches.
                      <br />
                      <button
                        onClick={handleReactivateSubscription}
                        disabled={processing}
                        style={{
                          marginTop: "12px",
                          background: "#10b981",
                          color: "white",
                          border: "none",
                          padding: "8px 16px",
                          borderRadius: "6px",
                          cursor: processing ? "not-allowed" : "pointer",
                          fontSize: "14px",
                          fontWeight: "500",
                        }}
                      >
                        Reactivate Subscription
                      </button>
                    </div>
                  )}
                  {gracePeriodActive && (
                    <div className="grace-notice">
                      ⚠️ Payment failed. Grace period ends on{" "}
                      {new Date(
                        subscriptionInfo.subscriptionGracePeriodEndsAt,
                      ).toLocaleDateString()}
                      . Please update your payment method.
                    </div>
                  )}
                </>
              )}

              {isOnTransaction && (
                <div className="transaction-notice">
                  Usage-based billing has been withdrawn. Your profile stays
                  hidden from customers until you subscribe.
                </div>
              )}
            </div>
          </div>

          {/* Subscription Benefits (if not already subscribed) */}
          {!isOnSubscription && (
            <div className="pricing-section">
              <h2>💎 Freelancer Subscription</h2>
              <p className="pricing-subtitle">
                Unlock unlimited bookings with zero transaction fees
              </p>

              <div className="freelancer-pricing-card">
                <div className="pricing-header">
                  <div className="pricing-icon">🚀</div>
                  <h3>Monthly Subscription</h3>
                  <div className="pricing-amount">
                    <span className="price">${monthlyPrice}</span>
                    <span className="period">/month</span>
                  </div>
                </div>

                <div className="pricing-features">
                  <h4>What's Included:</h4>
                  <ul>
                    <li>
                      <span className="feature-icon">✅</span>
                      <span>Unlimited service bookings</span>
                    </li>
                    <li>
                      <span className="feature-icon">✅</span>
                      <span>Zero transaction fees (0%)</span>
                    </li>
                    <li>
                      <span className="feature-icon">✅</span>
                      <span>Predictable monthly costs</span>
                    </li>
                    <li>
                      <span className="feature-icon">✅</span>
                      <span>Priority customer support</span>
                    </li>
                    <li>
                      <span className="feature-icon">✅</span>
                      <span>Advanced analytics dashboard</span>
                    </li>
                    <li>
                      <span className="feature-icon">✅</span>
                      <span>Customizable booking page</span>
                    </li>
                    <li>
                      <span className="feature-icon">✅</span>
                      <span>SMS & email notifications</span>
                    </li>
                    <li>
                      <span className="feature-icon">✅</span>
                      <span>Calendar integrations</span>
                    </li>
                  </ul>
                </div>

                <button
                  className="subscribe-btn"
                  onClick={() => navigate("/dashboard/freelancer/subscription/checkout")}
                  disabled={processing}
                >
                  {processing ? "Processing..." : "🚀 Subscribe Now"}
                </button>
              </div>
            </div>
          )}

          {/* Manage Subscription (if already subscribed) */}
          {isOnSubscription &&
            !subscriptionInfo?.subscriptionCancelAtPeriodEnd && (
              <div className="manage-section">
                <h2>Manage Subscription</h2>
                <div className="manage-actions">
                  <button
                    className="cancel-btn"
                    onClick={() => setShowCancelModal(true)}
                    disabled={processing}
                  >
                    Cancel Subscription
                  </button>
                  <p className="cancel-note">
                    You can cancel anytime. You'll keep access until the end of
                    your billing period.
                  </p>
                </div>
              </div>
            )}

          {/* A freelancer still on the withdrawn model (2026-11-01).
              Usage-based billing is no longer offered because the platform takes nothing on a service
              booking, so it was a free ride. They cannot be live without a subscription, so say so. */}
          {isOnTransaction && (
            <div className="transaction-info-card">
              <h3>Usage-based billing has been withdrawn</h3>
              <div className="transaction-details">
                <p>
                  OGESTYLE takes no per-booking fee on services, so this plan
                  was retired. Subscribe to become visible to customers again.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Subscribe Confirmation Modal */}
        {showSubscribeConfirmModal && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0,0,0,0.6)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 2000,
              padding: "20px",
            }}
            onClick={() => !processing && setShowSubscribeConfirmModal(false)}
          >
            <div
              style={{
                backgroundColor: "white",
                borderRadius: "16px",
                padding: "32px",
                maxWidth: "520px",
                width: "100%",
                boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
                fontFamily: "Inter, sans-serif",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={{ textAlign: "center", marginBottom: "24px" }}>
                <div style={{ fontSize: "48px", marginBottom: "16px" }}>💳</div>
                <h3
                  style={{
                    fontSize: "24px",
                    fontWeight: "700",
                    margin: "0 0 12px",
                    color: "#111827",
                  }}
                >
                  Start Your Subscription?
                </h3>
                <p
                  style={{
                    fontSize: "15px",
                    color: "#6b7280",
                    lineHeight: "1.6",
                    margin: 0,
                  }}
                >
                  You'll be redirected to Stripe to complete your payment setup
                </p>
              </div>

              <div
                style={{
                  background: "#f9fafb",
                  border: "1px solid #e5e7eb",
                  borderRadius: "12px",
                  padding: "20px",
                  marginBottom: "20px",
                }}
              >
                <div style={{ marginBottom: "16px" }}>
                  <div
                    style={{
                      fontSize: "32px",
                      fontWeight: "700",
                      color: "#10b981",
                      textAlign: "center",
                      marginBottom: "8px",
                    }}
                  >
                    ${platformSettings?.subscriptionFreelancerMonthly}
                    <span style={{ fontSize: "16px", fontWeight: "400" }}>
                      /month
                    </span>
                  </div>
                </div>

                <div
                  style={{
                    borderTop: "1px solid #e5e7eb",
                    paddingTop: "16px",
                  }}
                >
                  <div style={{ marginBottom: "12px" }}>
                    <div
                      style={{
                        fontSize: "13px",
                        color: "#6b7280",
                        marginBottom: "8px",
                      }}
                    >
                      What you'll get:
                    </div>
                    <ul
                      style={{
                        margin: 0,
                        paddingLeft: "20px",
                        fontSize: "14px",
                        color: "#374151",
                        lineHeight: "1.8",
                      }}
                    >
                      <li>Unlimited service bookings</li>
                      <li>Zero transaction fees (0%)</li>
                      <li>Public visibility on the platform</li>
                      <li>Priority customer support</li>
                    </ul>
                  </div>
                </div>
              </div>

              {promoEnabled && (
              <div
                style={{
                  background: "#f9fafb",
                  border: "1px solid #e5e7eb",
                  borderRadius: "8px",
                  padding: "12px",
                  marginBottom: "20px",
                }}
              >
                <label
                  htmlFor="freelancer-promo-code"
                  style={{
                    display: "block",
                    fontSize: "13px",
                    fontWeight: 600,
                    color: "#374151",
                    marginBottom: "8px",
                  }}
                >
                  Have a promo code?
                </label>
                <div style={{ display: "flex", gap: "8px" }}>
                  <input
                    id="freelancer-promo-code"
                    value={promoCode}
                    onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                    placeholder="Enter your code"
                    autoComplete="off"
                    style={{
                      flex: 1,
                      padding: "9px 12px",
                      border: "1px solid #d1d5db",
                      borderRadius: "8px",
                      fontSize: "14px",
                      textTransform: "uppercase",
                    }}
                  />
                  <button
                    type="button"
                    onClick={applyPromoCode}
                    disabled={validatingPromo}
                    style={{
                      padding: "9px 16px",
                      border: "none",
                      borderRadius: "8px",
                      background: "#0f766e",
                      color: "#ffffff",
                      fontWeight: 600,
                      cursor: "pointer",
                    }}
                  >
                    {validatingPromo ? "Checking..." : "Apply"}
                  </button>
                </div>
                {promoError && (
                  <p style={{ margin: "8px 0 0", fontSize: "12px", color: "#991b1b" }}>
                    {promoError}
                  </p>
                )}
                {promoPreview && (
                  <p style={{ margin: "8px 0 0", fontSize: "12px", color: "#166534", fontWeight: 600 }}>
                    {promoPreview.promoCode || promoCode} applied — {promoPreview.percentOff}% off
                    {promoPreview.durationMonths
                      ? ` for ${promoPreview.durationMonths} months`
                      : " with no end date"}.
                  </p>
                )}
              </div>
              )}
              <div
                style={{
                  background: "#fef3c7",
                  border: "1px solid #fbbf24",
                  borderRadius: "8px",
                  padding: "12px",
                  marginBottom: "24px",
                  fontSize: "13px",
                  color: "#78350f",
                  textAlign: "center",
                }}
              >
                💡 You can cancel anytime. Your billing starts after payment
                setup.
              </div>

              <div
                style={{
                  display: "flex",
                  gap: "12px",
                }}
              >
                <button
                  onClick={() => setShowSubscribeConfirmModal(false)}
                  disabled={processing}
                  style={{
                    flex: 1,
                    padding: "12px 24px",
                    fontSize: "15px",
                    fontWeight: "600",
                    border: "1px solid #d1d5db",
                    borderRadius: "8px",
                    backgroundColor: "white",
                    color: "#374151",
                    cursor: processing ? "not-allowed" : "pointer",
                    opacity: processing ? 0.5 : 1,
                    transition: "all 0.2s",
                  }}
                  onMouseEnter={(e) =>
                    !processing && (e.target.style.backgroundColor = "#f9fafb")
                  }
                  onMouseLeave={(e) =>
                    !processing && (e.target.style.backgroundColor = "white")
                  }
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubscribe}
                  disabled={processing}
                  style={{
                    flex: 1,
                    padding: "12px 24px",
                    fontSize: "15px",
                    fontWeight: "600",
                    border: "none",
                    borderRadius: "8px",
                    backgroundColor: "#10b981",
                    color: "white",
                    cursor: processing ? "not-allowed" : "pointer",
                    opacity: processing ? 0.5 : 1,
                    transition: "all 0.2s",
                  }}
                  onMouseEnter={(e) =>
                    !processing && (e.target.style.backgroundColor = "#059669")
                  }
                  onMouseLeave={(e) =>
                    !processing && (e.target.style.backgroundColor = "#10b981")
                  }
                >
                  {processing ? "Processing..." : "Continue to Payment"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Cancel Confirmation Modal */}
        {showCancelModal && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0,0,0,0.6)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 2000,
              padding: "20px",
            }}
            onClick={() => !processing && setShowCancelModal(false)}
          >
            <div
              style={{
                backgroundColor: "white",
                borderRadius: "16px",
                padding: "32px",
                maxWidth: "520px",
                width: "100%",
                boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
                fontFamily: "Inter, sans-serif",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={{ textAlign: "center", marginBottom: "24px" }}>
                <div style={{ fontSize: "48px", marginBottom: "16px" }}>⚠️</div>
                <h3
                  style={{
                    fontSize: "24px",
                    fontWeight: "700",
                    margin: "0 0 12px",
                    color: "#111827",
                  }}
                >
                  Cancel Subscription?
                </h3>
                <p
                  style={{
                    fontSize: "15px",
                    color: "#6b7280",
                    lineHeight: "1.6",
                    margin: 0,
                  }}
                >
                  Are you sure you want to cancel your subscription?
                </p>
              </div>

              <div
                style={{
                  background: "#fef2f2",
                  border: "1px solid #fecaca",
                  borderRadius: "12px",
                  padding: "20px",
                  marginBottom: "24px",
                }}
              >
                <div
                  style={{
                    fontSize: "14px",
                    color: "#991b1b",
                    lineHeight: "1.6",
                  }}
                >
                  <strong>What happens when you cancel:</strong>
                  <ul style={{ margin: "12px 0 0", paddingLeft: "20px" }}>
                    <li>
                      You'll keep access until{" "}
                      {subscriptionInfo?.subscriptionCurrentPeriodEnd &&
                        new Date(
                          subscriptionInfo.subscriptionCurrentPeriodEnd,
                        ).toLocaleDateString()}
                    </li>
                    <li>
                      After that, your profile goes inactive to customers and you
                      stop appearing in public searches. Usage-based billing has
                      been withdrawn, so a subscription is the only way to stay
                      live.
                    </li>
                    <li>
                      <strong>You'll stop appearing in public searches</strong>
                    </li>
                    <li>Your profile will become inactive to customers</li>
                  </ul>
                </div>
              </div>

              <div
                style={{
                  display: "flex",
                  gap: "12px",
                }}
              >
                <button
                  onClick={() => setShowCancelModal(false)}
                  disabled={processing}
                  style={{
                    flex: 1,
                    padding: "12px 24px",
                    fontSize: "15px",
                    fontWeight: "600",
                    border: "none",
                    borderRadius: "8px",
                    backgroundColor: "#10b981",
                    color: "white",
                    cursor: processing ? "not-allowed" : "pointer",
                    opacity: processing ? 0.5 : 1,
                    transition: "all 0.2s",
                  }}
                  onMouseEnter={(e) =>
                    !processing && (e.target.style.backgroundColor = "#059669")
                  }
                  onMouseLeave={(e) =>
                    !processing && (e.target.style.backgroundColor = "#10b981")
                  }
                >
                  Keep Subscription
                </button>
                <button
                  onClick={handleCancelSubscription}
                  disabled={processing}
                  style={{
                    flex: 1,
                    padding: "12px 24px",
                    fontSize: "15px",
                    fontWeight: "600",
                    border: "1px solid #d1d5db",
                    borderRadius: "8px",
                    backgroundColor: "white",
                    color: "#dc2626",
                    cursor: processing ? "not-allowed" : "pointer",
                    opacity: processing ? 0.5 : 1,
                    transition: "all 0.2s",
                  }}
                  onMouseEnter={(e) => {
                    if (!processing) {
                      e.target.style.backgroundColor = "#fef2f2";
                      e.target.style.borderColor = "#dc2626";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!processing) {
                      e.target.style.backgroundColor = "white";
                      e.target.style.borderColor = "#d1d5db";
                    }
                  }}
                >
                  {processing ? "Processing..." : "Yes, Cancel"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Cancel Success Modal */}
        {showCancelSuccessModal && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0,0,0,0.6)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 2000,
              padding: "20px",
            }}
            onClick={() => setShowCancelSuccessModal(false)}
          >
            <div
              style={{
                backgroundColor: "white",
                borderRadius: "16px",
                padding: "32px",
                maxWidth: "480px",
                width: "100%",
                boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
                fontFamily: "Inter, sans-serif",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={{ textAlign: "center", marginBottom: "24px" }}>
                <div style={{ fontSize: "64px", marginBottom: "16px" }}>✅</div>
                <h3
                  style={{
                    fontSize: "24px",
                    fontWeight: "700",
                    margin: "0 0 12px",
                    color: "#111827",
                  }}
                >
                  Subscription Canceled
                </h3>
                <p
                  style={{
                    fontSize: "15px",
                    color: "#6b7280",
                    lineHeight: "1.6",
                    margin: 0,
                  }}
                >
                  You can continue using the platform until the end of your
                  billing period.
                </p>
              </div>

              <div
                style={{
                  background: "#f0fdf4",
                  border: "1px solid #86efac",
                  borderRadius: "12px",
                  padding: "16px",
                  marginBottom: "24px",
                  textAlign: "center",
                }}
              >
                <div
                  style={{
                    fontSize: "13px",
                    color: "#166534",
                    marginBottom: "4px",
                  }}
                >
                  Access continues until
                </div>
                <div
                  style={{
                    fontSize: "18px",
                    fontWeight: "600",
                    color: "#059669",
                  }}
                >
                  {subscriptionInfo?.subscriptionCurrentPeriodEnd &&
                    new Date(
                      subscriptionInfo.subscriptionCurrentPeriodEnd,
                    ).toLocaleDateString()}
                </div>
              </div>

              <button
                onClick={() => setShowCancelSuccessModal(false)}
                style={{
                  width: "100%",
                  padding: "12px 24px",
                  fontSize: "15px",
                  fontWeight: "600",
                  border: "none",
                  borderRadius: "8px",
                  backgroundColor: "#10b981",
                  color: "white",
                  cursor: "pointer",
                  transition: "all 0.2s",
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = "#059669";
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = "#10b981";
                }}
              >
                OK
              </button>
            </div>
          </div>
        )}
      </div>
    </FreelancerSidebarLayout>
  );
}

export default FreelancerSubscription;
